import { forwardRef, useImperativeHandle, useState } from "react"
import {
  Modal,
  Box,
  Typography,
  Button,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  FormLabel,
  Paper,
  TextField,
} from "@mui/material"
import styles from "../Modals.module.css"
import ModalCloseButton from "../ModalCloseButton"
import T from "../../hooks/Language/T"
import useTranslationSharie from "../../hooks/Language/useTranslation"
import { useApiService } from "../../hooks/Api/useApiService"
import { useNavigate } from "react-router-dom"
import useError from "../../hooks/Error/useError"

const SurveyModal = forwardRef((props, ref) => {
  useImperativeHandle(ref, () => ({
    childFunction() {
      handleOpen()
    },
  }))
  const [open, setOpen] = useState(false)
  const t = useTranslationSharie()
  const { postData, getData } = useApiService()
  const navigate = useNavigate()
  const { Error: AlertError, info } = useError()

  const [formData, setFormData] = useState({
    likedFeatures: "",
    dislikedFeatures: "",
    reasonForStay: "",
    reasonForLeave: "",
    easeOfUse: "",
    recommended: "",
    favoriteSection: "",
    featureSuggestions: "",
    overallExperience: "",
    opinion: "",
    contact: "",
  })

  const handleChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }))
  }

  const handleSubmit = () => {
    const finalData = {
      ...formData,
      user_name: localStorage.getItem("user"),
    }

    postData("sharie/addsurvey", finalData)
    setTimeout(() => {
      info("Thank you for your feedback!")
      doSignOut()
    }, 2500)
  }

  const handleOpen = () => setOpen(true)
  const handleClose = () => {
    postData("sharie/markseensurvey", { user_name: localStorage.getItem("user") })
    if (props.signout) {
      doSignOut()
    }
    setOpen(false)
  }

  const doSignOut = async () => {
    try {
      await getData("logout")
    } catch (error) {}
    props.setIsAuthenticated(false)
    handleClose()
    navigate("/")
  }

  const radioQuestions = [
    {
      name: "likedFeatures",
      label: t("What did you like about Sharie?"),
      options: [t("Content"), t("Design"), t("Community"), t("Other")],
    },
    {
      name: "dislikedFeatures",
      label: t("Did you dislike anything?"),
      options: [t("Bugs"), t("Design"), t("Confusing"), t("Nothing")],
    },
    {
      name: "reasonForStay",
      label: t("What made you stay?"),
      options: [t("Valuable content"), t("Easy to use"), t("Fun"), t("Other")],
    },
    {
      name: "reasonForLeave",
      label: t("Did anything make you leave, why?"),
      options: [t("Not enough value"), t("Bugs"), t("Confusing"), t("Other")],
    },
    {
      name: "easeOfUse",
      label: t("How easy was it to use Sharie?"),
      options: [t("Very difficult"), t("Difficult"), t("Okay"), t("Easy"), t("Very easy")],
    },
    {
      name: "recommended",
      label: t("Would you recommend Sharie to a friend?"),
      options: [t("Yes"), t("Maybe"), t("No")],
    },
    {
      name: "favoriteSection",
      label: t("What was the most useful section for you?"),
      options: [t("Skill search"), t("Posts"), t("Messaging"), t("Other")],
    },
    {
      name: "featureSuggestions",
      label: t("What features would you like us to add?"),
      options: [t("More skills"), t("Better chat"), t("More Groups"), t("Other")],
    },
    {
      name: "overallExperience",
      label: t("How was your overall experience?"),
      options: [t("Bad"), t("Okay"), t("Good"), t("Excellent")],
    },
  ]

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby='modal-title'
      aria-describedby='modal-description'
    >
      <T>
        <ModalCloseButton handleClose={handleClose} />
        <Box className={styles.modal_container} sx={{ overflowY: "auto", maxHeight: "90vh" }}>
          <Paper
            elevation={3}
            sx={{ p: 4, maxWidth: 700, mx: "auto", my: 4, bgcolor: "text.primary" }}
          >
            <Typography variant='h4' color='primary' gutterBottom>
              {t("Feedback")}
            </Typography>
            <Typography variant='h6' color='primary' gutterBottom>
              {t("We would appreciate your opinion to improve Sharie!")}
            </Typography>

            <Box display='flex' flexDirection='column' gap={3}>
              {radioQuestions.map((q) => (
                <FormControl key={q.name}>
                  <FormLabel>{q.label}</FormLabel>
                  <RadioGroup name={q.name} value={formData[q.name]} onChange={handleChange}>
                    {q.options.map((opt, idx) => (
                      <FormControlLabel key={idx} value={opt} control={<Radio />} label={opt} />
                    ))}
                  </RadioGroup>
                </FormControl>
              ))}
              <TextField
                label={t("We would love to hear your opinion or ideas!")}
                name='opinion'
                value={formData.opinion}
                onChange={handleChange}
                fullWidth
                multiline
              />
              <TextField
                label={t("Contact info (if you'd like us to reach out)")}
                name='contact'
                value={formData.contact}
                onChange={handleChange}
                fullWidth
                multiline
              />

              <Button variant='contained' color='primary' onClick={handleSubmit}>
                {t("Submit Feedback")}
              </Button>
              <AlertError />
            </Box>
          </Paper>
        </Box>
      </T>
    </Modal>
  )
})

export default SurveyModal
